import { useRef, useState } from 'react';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import usePageContent from '../utils/usePageContent';

const images = [
    "/images/contact-us/contactusimage.png" 
];

const ContactUs = () => {
    const form = useRef();
    const [isSending, setIsSending] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { title, description } = usePageContent('contact');


    const sendEmail = (e) => {
        e.preventDefault();
        setIsSending(true);
        setIsSuccess(false);
        setIsError(false);
        setErrorMessage('');

        // Get form data for debugging
        const formData = new FormData(form.current);
        const formValues = Object.fromEntries(formData.entries());

        emailjs.sendForm(
            // 'service_r6he249',
            // 'template_qwg7f1e', 
            'service_ym2zebl', //enso serviceid
            'template_7nmfja3', // enso template
            form.current,
            'FYiFoxlMpd8MNSV5N' // enso publickey
            // 'gsSBvMBvA0Bl20dXY' 
        )
        .then((result) => {
            setIsSuccess(true);
            form.current.reset();
        })
        .catch((error) => {
            console.error('Email send failed:', {
                status: error.status,
                message: error.text,
                details: error
            });
            setIsError(true);
            setErrorMessage(error.text || 'Failed to send message. Please try again later.');
        })
        .finally(() => {
            setIsSending(false);
        });
    };

    return (
        <div className="relative min-h-screen bg-white font-sans">
            {/* Hero Image Section */}
            <div className="relative h-[600px] w-full">
                <img 
                    src={images[0]}  
                    alt="Contact Us" 
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center text-white px-8">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 relative after:content-[''] after:block after:w-full after:h-1 after:bg-red-600 after:mt-2">
                        {title}
                    </h2>
                    <p className="text-lg max-w-2xl text-center leading-relaxed font-medium">
                       {description}
                    </p>
                </div>
            </div>

            {/* Contact Boxes */}
            <div className="absolute top-[550px] left-1/2 transform -translate-x-1/2 w-full max-w-5xl px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Address Box */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-center h-64 w-full">
                    <div className="border-2 border-gray-300 rounded-full p-3 mb-4">
                        <FaMapMarkerAlt className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-red-600 mb-2 uppercase">Address</h3>
                    <p className="text-gray-800 font-medium text-sm">A1 112, VIDA COMPLEX ABOVE KFC, PANJIM
                        PONDA ROAD , KADAMBA PLATEAU , GOA
                        403206
                    </p>
                </div>

                {/* Phone Box */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-center h-64 w-full">
                    <div className="border-2 border-gray-300 rounded-full p-3 mb-4">
                        <FaPhoneAlt className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-red-600 mb-2 uppercase">Mobile Number</h3>
                    <p className="text-gray-800 font-medium text-sm">+91 99676 06321</p>
                </div>

                {/* Email Box */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-center h-64 w-full">
                    <div className="border-2 border-gray-300 rounded-full p-3 mb-4">
                        <FaEnvelope className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-bold text-red-600 mb-2 uppercase">Email Id</h3>
                    <p className="text-gray-800 font-medium text-sm">enso.photography.design@gmail.com</p>
                </div>
            </div>

            {/* Contact Form - Enlarged */}
            <div className="max-w-5xl mx-auto mt-72 mb-20 bg-white shadow-xl rounded-xl p-10 border-2 border-red-300">
                <h2 className="text-3xl font-bold text-red-600 mb-8">Send Message</h2>
                
                {/* Status Messages */}
                {isSuccess && (
                    <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg">
                        Message sent successfully! We'll get back to you soon.
                    </div>
                )}
                
                {isError && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg">
                        {errorMessage || 'Failed to send message. Please try again later.'}
                        <p className="mt-2 text-sm">
                            If the problem persists, please email us directly at enso.photography.design@gmail.com
                        </p>
                    </div>
                )}
                
                <form ref={form} onSubmit={sendEmail} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="first_name" className="block text-lg font-medium text-gray-700 mb-2">
                                First Name
                            </label>
                            <input 
                                type="text" 
                                id="first_name"
                                name="firstname"
                                placeholder="First Name" 
                                className="border-2 border-gray-300 rounded-lg p-4 w-full bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="last_name" className="block text-lg font-medium text-gray-700 mb-2">
                                Last Name
                            </label>
                            <input 
                                type="text" 
                                id="last_name"
                                name="lastname"
                                placeholder="Last Name" 
                                className="border-2 border-gray-300 rounded-lg p-4 w-full bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="topic" className="block text-lg font-medium text-gray-700 mb-2">
                                Topic
                            </label>
                            <input 
                                type="text" 
                                id="topic"
                                name="topic"
                                placeholder="Topic" 
                                className="border-2 border-gray-300 rounded-lg p-4 w-full bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="email" className="block text-lg font-medium text-gray-700 mb-2">
                                Email
                            </label>
                            <input 
                                type="email" 
                                id="email"
                                name="email"
                                placeholder="Email" 
                                className="border-2 border-gray-300 rounded-lg p-4 w-full bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="message" className="block text-lg font-medium text-gray-700 mb-2">
                            Phone Number
                        </label>
                        <input 
                            id="phone"
                            name="phone"
                            placeholder="Phone Number" 
                            className="border-2 border-gray-300 rounded-lg p-4 w-full  bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            required
                        ></input>
                    </div>
                    
                    <div>
                        <label htmlFor="message" className="block text-lg font-medium text-gray-700 mb-2">
                            Message
                        </label>
                        <textarea 
                            id="message"
                            name="message"
                            placeholder="Message..." 
                            className="border-2 border-gray-300 rounded-lg p-4 w-full h-48 bg-gray-100 text-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            required
                        ></textarea>
                    </div>
                    
                    <div className="flex justify-end">
                        <button 
                            type="submit" 
                            disabled={isSending}
                            className={`bg-red-600 text-white text-xl w-64 py-4 rounded-lg text-center hover:bg-red-700 transition-colors flex items-center justify-center ${
                                isSending ? 'opacity-70 cursor-not-allowed' : ''
                            }`}
                        >
                            {isSending ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Sending...
                                </>
                            ) : (
                                'Send Message'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactUs;